import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import MultiSelect from "@/components/MultiSelect";
import { ArrowLeft, FileSpreadsheet, Sheet, FileText, Table2, Loader2, Columns3, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

const LONG = ["diagnostico", "trabajo_realizado", "causa_raiz", "observaciones", "recomendaciones", "repuestos", "repuestos_sugeridos", "repuesto_solicitar", "direccion"];

export default function DetalleServicios() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [resumen, setResumen] = useState({ total: 0, preventivas: 0, correctivas: 0, completados: 0 });
  // Opciones cascade: cada filtro tiene opciones dinámicas según los demás filtros aplicados
  const [facets, setFacets] = useState({
    cliente: [], sucursal: [], tipo: [], tecnico: [], estado: [], tipo_asistencia: [],
  });
  const [filtrosExtra, setFiltrosExtra] = useState([]); // [{key,label}]
  const [allColumns, setAllColumns] = useState([]);
  const [selectedCols, setSelectedCols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [includeDashboard, setIncludeDashboard] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  // null = "todos seleccionados por defecto" (sin filtro). Array = selección explícita del usuario.
  const [filters, setFilters] = useState({
    cliente: null, tipo: null, estado: null, sucursal: null, tecnico: null,
    tipo_asistencia: null, fecha_desde: "", fecha_hasta: "",
  });
  const [extra, setExtra] = useState({}); // filtros avanzados por columna
  const firstLoad = useRef(true);
  const reqIdRef = useRef(0); // descarta respuestas viejas (race condition)

  const cleanExtra = useCallback((ex) => {
    const out = {};
    Object.entries(ex || {}).forEach(([k, v]) => { if (v && String(v).trim()) out[k] = v; });
    return out;
  }, []);

  // Empaqueta los filtros para el backend: null = sin filtro, array = $in.
  const filtrosPayload = useCallback((f, ex) => ({
    tipo: f.tipo, estado: f.estado, cliente: f.cliente, sucursal: f.sucursal,
    tecnico: f.tecnico, tipo_asistencia: f.tipo_asistencia,
    fecha_desde: f.fecha_desde || null, fecha_hasta: f.fecha_hasta || null,
    extra: cleanExtra(ex),
  }), [cleanExtra]);

  const buildParams = useCallback((f, ex) => {
    const p = new URLSearchParams();
    ["cliente", "tipo", "estado", "sucursal", "tecnico", "tipo_asistencia"].forEach((k) => {
      const v = f[k];
      if (Array.isArray(v) && v.length > 0) v.forEach((x) => p.append(k, x));
    });
    if (f.fecha_desde) p.set("fecha_desde", f.fecha_desde);
    if (f.fecha_hasta) p.set("fecha_hasta", f.fecha_hasta);
    const ce = cleanExtra(ex);
    if (Object.keys(ce).length) p.set("extra", JSON.stringify(ce));
    return p.toString();
  }, [cleanExtra]);

  const load = useCallback(async (f, ex) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      // Datos y facetas en paralelo; descartamos respuestas viejas si llega una más nueva.
      const [dataR, facetsR] = await Promise.all([
        api.get(`/api/detalle-servicios?${buildParams(f, ex)}`),
        api.post(`/api/detalle-servicios/facetas`, { filtros: filtrosPayload(f, ex) }),
      ]);
      if (reqId !== reqIdRef.current) return; // respuesta vieja: descartar
      const data = dataR.data;
      setRows(data.rows || []);
      setResumen(data.resumen || {});
      if (data.columns) setAllColumns(data.columns);
      setSelectedCols((prev) => (prev.length ? prev : (data.default || [])));
      setSelected(new Set((data.rows || []).map((x) => x.id))); // filas seleccionadas por defecto
      setFacets(facetsR.data || {});
    } catch {
      if (reqId === reqIdRef.current) toast.error("Error al cargar el detalle de servicios");
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [api, buildParams, filtrosPayload]);

  // Carga inicial de catálogos auxiliares (solo los filtros avanzados — el resto sale de facetas)
  useEffect(() => {
    api.get("/api/detalle-servicios/opciones").then((r) => {
      setFiltrosExtra(r.data.filtros_extra || []);
    }).catch(() => {});
  }, [api]);

  // Auto-recorte de selecciones cuando cambian las facetas: si un valor quedó
  // fuera del nuevo dominio (por aplicar otro filtro), se quita. Si quedó la
  // selección completa o vacía, vuelve a "todas por defecto" (null).
  useEffect(() => {
    setFilters((prev) => {
      let changed = false;
      const next = { ...prev };
      ["cliente", "sucursal", "tipo", "tecnico", "estado", "tipo_asistencia"].forEach((k) => {
        if (!Array.isArray(prev[k])) return; // null = todas por defecto, no tocar
        const opts = (facets[k] || []).map((o) => (typeof o === "string" ? o : o.value));
        const trimmed = prev[k].filter((v) => opts.includes(v));
        if (trimmed.length === 0 || trimmed.length === opts.length) {
          next[k] = null; changed = true;
        } else if (trimmed.length !== prev[k].length) {
          next[k] = trimmed; changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [facets]);

  // Auto-aplicar filtros (con pequeño debounce)
  useEffect(() => {
    const t = setTimeout(() => load(filters, extra), firstLoad.current ? 0 : 300);
    firstLoad.current = false;
    return () => clearTimeout(t);
  }, [filters, extra, load]);

  const ue = (k, v) => setExtra((p) => ({ ...p, [k]: v }));
  // Setter de filtros multi-select: normaliza a null cuando se seleccionan todos o ninguno.
  const setMulti = (k, newSel) => setFilters((prev) => {
    const opts = (facets[k] || []).map((o) => (typeof o === "string" ? o : o.value));
    const isAll = opts.length > 0 && newSel.length === opts.length;
    const isNone = newSel.length === 0;
    return { ...prev, [k]: (isAll || isNone) ? null : newSel };
  });
  // Setters de campos simples (fechas)
  const uf = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  // Selección actual mostrada al MultiSelect: si es null, mostrar todas como seleccionadas.
  const shown = (k) => {
    const opts = (facets[k] || []).map((o) => (typeof o === "string" ? o : o.value));
    return Array.isArray(filters[k]) ? filters[k] : opts;
  };
  const clearFilters = () => {
    setFilters({ cliente: null, tipo: null, estado: null, sucursal: null, tecnico: null, tipo_asistencia: null, fecha_desde: "", fecha_hasta: "" });
    setExtra({});
  };

  const toggleCol = (key) => setSelectedCols((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  const colLabel = (key) => allColumns.find((c) => c.key === key)?.label || key;
  const orderedSelected = allColumns.filter((c) => selectedCols.includes(c.key)).map((c) => c.key);

  // Selección de filas
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleRow = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const download = async (formato) => {
    if (orderedSelected.length === 0) { toast.error("Selecciona al menos una columna"); return; }
    if (selected.size === 0) { toast.error("Selecciona al menos una fila"); return; }
    setDownloading(formato);
    try {
      const exclude_ids = rows.filter((r) => !selected.has(r.id)).map((r) => r.id);
      const body = {
        formato,
        columns: orderedSelected,
        filtros: filtrosPayload(filters, extra),
        exclude_ids,
        incluir_dashboard: formato === "pdf" ? includeDashboard : false,
      };
      const r = await api.post("/api/detalle-servicios/export", body, { responseType: "blob" });
      const meta = { excel: ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
                     csv: ["csv", "text/csv"], pdf: ["pdf", "application/pdf"] }[formato];
      const url = window.URL.createObjectURL(new Blob([r.data], { type: meta[1] }));
      const a = document.createElement("a");
      a.href = url; a.download = `detalle_servicios.${meta[0]}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(formato === "csv" ? "CSV descargado (ábrelo en Google Sheets)" : `${formato.toUpperCase()} descargado`);
    } catch { toast.error("Error al descargar"); }
    finally { setDownloading(""); }
  };

  const cellContent = (key, r) => {
    if (key === "tipo") return <Badge className={r.tipo_raw === "correctiva" ? "bg-[#384091] text-white border-0" : "bg-emerald-600 text-white border-0"}>{r.tipo_raw === "correctiva" ? "MC" : "MP"}</Badge>;
    if (key === "estado") return <Badge className={r.estado === "Completado" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-amber-50 text-amber-700 border-0"}>{r.estado}</Badge>;
    if (key === "folio") return <span className="font-mono text-[#0FB4B7] font-semibold whitespace-nowrap">{r.folio}</span>;
    const v = r[key];
    const isLong = LONG.includes(key);
    return <span className={isLong ? "block max-w-[240px] truncate" : "whitespace-nowrap"} title={typeof v === "string" ? v : ""}>{v || "—"}</span>;
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Table2 className="h-5 w-5 text-[#0FB4B7]" />
            <span className="text-sm font-semibold text-[#384091]">Detalle de Servicios</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
            <ArrowLeft className="h-4 w-4" /><span className="ml-1">Volver</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Resumen */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total servicios", value: resumen.total, color: "#384091" },
            { label: "Preventivas", value: resumen.preventivas, color: "#0FB4B7" },
            { label: "Correctivas", value: resumen.correctivas, color: "#534AB7" },
            { label: "Completados", value: resumen.completados, color: "#059669" },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value || 0}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filtros */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <p className="text-[11px] text-gray-400">Por defecto todos los valores están seleccionados. Desmarca los que no quieras incluir. Las opciones se ajustan automáticamente según los demás filtros.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <div><Label className="text-xs">Cliente</Label>
                <MultiSelect options={facets.cliente || []} selected={shown("cliente")} onChange={(v) => setMulti("cliente", v)} placeholder="Sin opciones" /></div>
              <div><Label className="text-xs">Sucursal</Label>
                <MultiSelect options={facets.sucursal || []} selected={shown("sucursal")} onChange={(v) => setMulti("sucursal", v)} placeholder="Sin opciones" /></div>
              <div><Label className="text-xs">Tipo de servicio</Label>
                <MultiSelect options={facets.tipo || []} selected={shown("tipo")} onChange={(v) => setMulti("tipo", v)} placeholder="Sin opciones" /></div>
              <div><Label className="text-xs">Técnico</Label>
                <MultiSelect options={facets.tecnico || []} selected={shown("tecnico")} onChange={(v) => setMulti("tecnico", v)} placeholder="Sin opciones" /></div>
              <div><Label className="text-xs">Estado</Label>
                <MultiSelect options={facets.estado || []} selected={shown("estado")} onChange={(v) => setMulti("estado", v)} placeholder="Sin opciones" /></div>
              <div><Label className="text-xs">Tipo de asistencia</Label>
                <MultiSelect options={facets.tipo_asistencia || []} selected={shown("tipo_asistencia")} onChange={(v) => setMulti("tipo_asistencia", v)} placeholder="Sin opciones" /></div>
              <div><Label className="text-xs">Fecha desde</Label>
                <Input type="date" value={filters.fecha_desde} onChange={(e) => uf("fecha_desde", e.target.value)} className="h-9 text-xs" /></div>
              <div><Label className="text-xs">Fecha hasta</Label>
                <Input type="date" value={filters.fecha_hasta} onChange={(e) => uf("fecha_hasta", e.target.value)} className="h-9 text-xs" /></div>
            </div>

            {/* Filtros avanzados (+) */}
            {showAdvanced && (
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-[#384091] mb-2">Filtros adicionales por columna</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {filtrosExtra.map((c) => (
                    <div key={c.key}><Label className="text-xs">{c.label}</Label>
                      <Input value={extra[c.key] || ""} onChange={(e) => ue(c.key, e.target.value)} placeholder="Contiene..." className="h-9 text-xs" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center">
              <Button variant="outline" size="sm" onClick={() => setShowAdvanced((s) => !s)}>
                {showAdvanced ? <Minus className="h-3.5 w-3.5 mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                {showAdvanced ? "Menos filtros" : "Más filtros"}
              </Button>
              <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar</Button>

              {/* Selector de columnas */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns3 className="h-3.5 w-3.5 mr-1" /> Columnas ({orderedSelected.length})
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-3" align="start">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-[#384091]">Columnas a mostrar/descargar</span>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setSelectedCols(allColumns.map((c) => c.key))}>Todas</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setSelectedCols([])}>Ninguna</Button>
                  </div>
                  <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                    {allColumns.map((c) => (
                      <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                        <Checkbox checked={selectedCols.includes(c.key)} onCheckedChange={() => toggleCol(c.key)} />
                        <span className="text-gray-700">{c.label}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Dashboard en PDF */}
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer ml-1">
                <Checkbox checked={includeDashboard} onCheckedChange={(v) => setIncludeDashboard(!!v)} />
                Dashboard en PDF
              </label>

              <div className="flex-1" />
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => download("excel")} disabled={!!downloading}>
                {downloading === "excel" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />}
                Excel
              </Button>
              <Button size="sm" className="bg-[#0F9D58] hover:bg-[#0c7d46] text-white"
                onClick={() => download("csv")} disabled={!!downloading}>
                {downloading === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sheet className="h-3.5 w-3.5 mr-1" />}
                Google Sheets
              </Button>
              <Button size="sm" className="bg-[#384091] hover:bg-[#2D3375] text-white"
                onClick={() => download("pdf")} disabled={!!downloading}>
                {downloading === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileText className="h-3.5 w-3.5 mr-1" />}
                PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabla */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <p className="text-center text-gray-400 py-12">Cargando...</p>
            ) : rows.length === 0 ? (
              <p className="text-center text-gray-400 py-12">Sin servicios para los filtros aplicados</p>
            ) : orderedSelected.length === 0 ? (
              <p className="text-center text-gray-400 py-12">Selecciona al menos una columna</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#384091] text-white">
                    <th className="px-3 py-2 w-8">
                      <Checkbox className="border-white data-[state=checked]:bg-white data-[state=checked]:text-[#384091]"
                        checked={allSelected} onCheckedChange={toggleAll} />
                    </th>
                    {orderedSelected.map((k) => (
                      <th key={k} className="text-left px-3 py-2 font-medium whitespace-nowrap">{colLabel(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const isSel = selected.has(r.id);
                    return (
                      <tr key={r.id || i} className={`border-b last:border-0 align-top ${!isSel ? "opacity-40" : ""} ${i % 2 ? "bg-[#F5F7FA]" : ""}`}>
                        <td className="px-3 py-2">
                          <Checkbox checked={isSel} onCheckedChange={() => toggleRow(r.id)} />
                        </td>
                        {orderedSelected.map((k) => (
                          <td key={k} className="px-3 py-2">{cellContent(k, r)}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
        {!loading && rows.length > 0 && (
          <p className="text-xs text-gray-400 text-center">
            {selected.size} de {rows.length} fila(s) seleccionadas para descargar · {orderedSelected.length} columna(s)
          </p>
        )}
      </main>
    </div>
  );
}
